/**
 * API 檢視模式的浮出卡片：一筆註冊表內容（打哪支、輸入、輸出、資料、權限）。
 * 桌機：貼在元件下方（下面放不下就放上面），pointer-events: none，不擋點擊；
 * 手機（長按開的）：從底部滑出的面板，可以捲動，點面板外面收起。
 * 配色只用既有 tokens（主色、品牌青、珊瑚、酒紅、muted）。
 */
"use client";

import { useLayoutEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import type { CallKind, DataRef, InspectEntry, SourceKind } from "@/lib/inspect/types";
import { cn } from "@/lib/utils";

/** 動作類型：標籤用白話，desc 寫這種動作實際會發生什麼（技術名稱放括號） */
const KIND: Record<CallKind, { label: string; className: string; desc: string }> = {
  action: { label: "送到伺服器", className: "bg-primary text-primary-foreground", desc: "按下後交給伺服器執行（Server Action），Network 面板看到的是 POST 目前網址、標頭帶 Next-Action" },
  route: { label: "打網址", className: "bg-brand-teal text-white", desc: "直接打一支網址取得結果，例如下載檔案（Route Handler）" },
  page: { label: "換頁", className: "bg-secondary text-secondary-foreground ring-1 ring-border", desc: "換到另一頁：伺服器讀好資料再把畫面送回來" },
  legacy: { label: "列管 API", className: "bg-brand-coral text-white", desc: "舊站列管的 API（/api、/push），轉給 CL-API 處理" },
  client: { label: "源頭 GET", className: "bg-secondary text-secondary-foreground ring-1 ring-border", desc: "資料是進這頁時下面這支請求帶來的；在這裡選、篩選、填欄位不會再發請求，按下送出鍵才會送" },
};

/** 資料來源的標籤；desc 是卡片底下的說明（只寫需要解釋的） */
const SOURCE: Record<SourceKind, { label: string; className: string; desc?: string }> = {
  srv: { label: "srv_web", className: "bg-primary/10 text-primary", desc: "srv_web：從 .13 複製過來的資料快照" },
  banwu: { label: "政龍", className: "bg-brand-teal/15 text-[color-mix(in_oklch,var(--color-brand-teal),black_30%)]" },
  clapi: { label: "CL-API", className: "bg-brand-coral/15 text-[color-mix(in_oklch,var(--color-brand-coral),black_35%)]", desc: "CL-API：複製 .13 過來的 API" },
  ext: { label: "外部", className: "bg-brand-maroon/10 text-brand-maroon" },
  file: { label: "檔案", className: "bg-muted text-foreground" },
  cookie: { label: "cookie", className: "bg-muted text-muted-foreground" },
};

/** 「源頭 GET」：這頁載入時的那支請求（provider 從頁面大標題的 .page 元件找） */
export interface PageSource { http: string; fn?: string; file?: string }

const W = 380;
const GAP = 8;

export function InspectCard({ id, entry, loading, href, anchor, sheet, source, onClose }: {
  id: string;
  source: PageSource | null;
  entry: InspectEntry | undefined;
  loading: boolean;
  href: string | null;
  anchor: DOMRect;
  sheet: boolean;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // 桌機：量過卡片高度再決定放元件下面還是上面，左右夾在視窗內
  useLayoutEffect(() => {
    if (sheet || !ref.current) return;
    const h = ref.current.offsetHeight;
    const vw = window.innerWidth, vh = window.innerHeight;
    const w = Math.min(W, vw - GAP * 2);
    const left = Math.min(Math.max(anchor.left, GAP), vw - w - GAP);
    let top = anchor.bottom + GAP;
    if (top + h > vh - GAP) top = anchor.top - GAP - h;
    if (top < GAP) top = Math.max(GAP, vh - h - GAP);
    // 要先量到卡片高度才能決定位置
    setPos({ left, top });
  }, [anchor, sheet, id, entry]);

  const body = (
    <div className="flex flex-col gap-2.5 text-xs leading-relaxed">
      {loading ? (
        <p className="text-muted-foreground">載入註冊表…</p>
      ) : !entry ? (
        <p className="font-medium text-destructive">註冊表還沒有這個元件：<code className="font-mono">{id}</code></p>
      ) : (
        <>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className={cn("rounded px-1.5 py-0.5 text-[11px] font-semibold", KIND[entry.call.kind].className)}>{KIND[entry.call.kind].label}</span>
                <span className="text-sm font-semibold text-foreground">{entry.label}</span>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">{KIND[entry.call.kind].desc}</p>
            </div>
            {sheet && (
              <button type="button" onClick={onClose} aria-label="關閉" className="-m-1 rounded p-1 text-muted-foreground hover:bg-muted">
                <X className="size-4" />
              </button>
            )}
          </div>

          {entry.call.kind === "client" ? (
            <>
              {source && <CallLine http={source.http} fn={source.fn} file={source.file} />}
              {entry.call.fn && <Row label="畫面"><span className="font-mono text-[11px] break-all text-muted-foreground">{entry.call.fn}{entry.call.file && `　${entry.call.file}`}</span></Row>}
            </>
          ) : (
            <CallLine http={entry.call.http} fn={entry.call.fn} file={entry.call.file} />
          )}
          {href && <Row label="這一個"><code className="font-mono break-all">GET {href}</code></Row>}
          {entry.also?.map((c, i) => <CallLine key={i} http={c.http} fn={c.fn} file={c.file} also />)}

          {entry.input && <Row label="輸入"><List items={entry.input} /></Row>}
          {entry.output && <Row label="輸出"><List items={entry.output} /></Row>}
          {entry.data && <Row label="資料"><Sources data={entry.data} /></Row>}
          {entry.perm && <Row label="權限"><span className="text-foreground">{entry.perm}</span></Row>}
          {entry.note && <p className="rounded bg-muted/70 px-2 py-1.5 text-muted-foreground">{entry.note}</p>}
          <p className="font-mono text-[10px] text-muted-foreground/70">{id}</p>
        </>
      )}
    </div>
  );

  if (sheet) {
    return (
      <div
        ref={ref}
        role="dialog"
        aria-label="API 檢視"
        className="fixed inset-x-0 bottom-0 z-[210] max-h-[60vh] animate-in overflow-y-auto rounded-t-xl border-t bg-popover p-4 pb-6 text-popover-foreground shadow-2xl duration-200 slide-in-from-bottom"
      >
        {body}
      </div>
    );
  }
  return (
    <div
      ref={ref}
      role="tooltip"
      className="pointer-events-none fixed z-[210] max-h-[calc(100vh-16px)] overflow-hidden rounded-lg border bg-popover p-3 text-popover-foreground shadow-xl"
      style={{ width: `min(${W}px, calc(100vw - ${GAP * 2}px))`, left: pos?.left ?? -9999, top: pos?.top ?? 0, visibility: pos ? "visible" : "hidden" }}
    >
      {body}
    </div>
  );
}

/** 註冊表寫「目前網址」的地方（Server Action 打的就是這一頁本身），顯示時換成實際路徑，避免以為有另一支 API */
const withPath = (http: string) =>
  http.replace("目前網址", `${window.location.pathname}（就是這一頁的網址）`);

function CallLine({ http, fn, file, also }: { http?: string; fn?: string; file?: string; also?: boolean }) {
  if (!http && !fn) return null;
  return (
    <div className={cn("rounded-md bg-muted/60 px-2 py-1.5 font-mono text-[11px]", also && "opacity-80")}>
      {http && <div className="font-semibold break-all text-foreground">{also && "＋ "}{withPath(http)}</div>}
      {fn && <div className="break-all text-muted-foreground">{fn}{file && <span className="opacity-80">　{file}</span>}</div>}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[2.5rem_1fr] gap-2">
      <span className="font-medium text-muted-foreground">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function List({ items }: { items: string[] }) {
  if (items.length === 1) return <span className="text-foreground">{items[0]}</span>;
  return (
    <ul className="flex list-disc flex-col gap-0.5 pl-3.5 text-foreground marker:text-muted-foreground">
      {items.map((t) => <li key={t}>{t}</li>)}
    </ul>
  );
}

function Sources({ data }: { data: DataRef[] }) {
  // 用到 srv_web、CL-API 時在清單下面註明它們是什麼（.13 的快照／複製的 API）
  const notes = [...new Set(data.map((d) => SOURCE[d.kind].desc).filter(Boolean))];
  return (
    <>
    <ul className="flex flex-col gap-1">
      {data.map((d) => (
        <li key={`${d.kind}-${d.name}-${d.mode}`} className="flex flex-wrap items-baseline gap-1.5">
          <span className={cn("rounded px-1 py-px text-[10px] font-semibold", SOURCE[d.kind].className)}>{SOURCE[d.kind].label}</span>
          <span className="font-mono text-[11px] text-foreground">{d.name}</span>
          <span className={cn("text-[11px]", d.mode === "讀" ? "text-muted-foreground" : "font-semibold text-brand-maroon")}>{d.mode}</span>
          {d.note && <span className="w-full pl-0.5 text-muted-foreground">{d.note}</span>}
        </li>
      ))}
    </ul>
    {notes.length > 0 && <p className="mt-1.5 text-[11px] text-muted-foreground">{notes.join("；")}</p>}
    </>
  );
}
